import { getIn, useField, useFormikContext } from "formik";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { Badge, Col, Container, Row } from "react-bootstrap";
import { object } from "yup";
import AccordionCustom, {
  useAccordionState,
} from "../../../components/Accordion/Accordion.tsx";
import { useAppContext } from "../../../components/App/AppProvider.tsx";
import SeeMoreButton from "../../../components/Button/SeeMoreButton.tsx";
import PageTitle from "../../../components/Elements/PageTitle/PageTitle.tsx";
import { useGet } from "../../../components/Fetch/useFetch.tsx";
import PositiveNumberInput, {
  CheckBoxInput,
  ColorInput,
  FormContainer,
  Multiselect,
  SelectInput,
  TextAreaInput,
  TextInput,
} from "../../../components/Form/Form.tsx";
import MyFormik from "../../../components/Form/MyFormik.tsx";
import SubmitFormButtons from "../../../components/Form/SubmitFormButtons.tsx";
import TransferList, {
  useTransferList,
} from "../../../components/Form/TransferList.tsx";
import { IconParametre } from "../../../components/Icon/Icon.tsx";
import { hasDroit } from "../../../droits.tsx";
import typeAgent from "../../../Entities/TypeAgentEntity.tsx";
import { referenceColumnPei } from "../../../enums/ColumnPeiEnum.tsx";
import referenceDfciListeCouche, {
  DFCI_LISTE_COUCHE,
} from "../../../enums/DfciListeCoucheEnum.tsx";
import TYPE_DROIT from "../../../enums/DroitEnum.tsx";
import typeAffichageCoordonnees from "../../../enums/TypeAffichageCoordonnees.tsx";
import typeRouteHistoriquePei from "../../../enums/TypeRouteHistoriquePei.tsx";
import TYPE_PARAMETRE from "../../../enums/TypesParametres.tsx";
import url from "../../../module/fetch.tsx";
import {
  requiredBoolean,
  requiredNumber,
  requiredString,
} from "../../../module/validators.tsx";
import { IdCodeLibelleType } from "../../../utils/typeUtils.tsx";
import TypeOrganismeMultiSelectParam from "./TypeOrganismeMultiSelectParam.tsx";

type ParametresSectionGeneral = {
  mentionCnil: string;
  messageEntete: string;
  titrePage: string;
  toleranceVoiesMetres: number;
  accueilPublic: string | undefined;
};

type ParametresSectionSignalement = {
  signalementDeliberationDestinataireEmail: string;
  signalementDeliberationCorpsEmail: string;
  signalementDeliberationObjetEmail: string;
};

type ParametresSectionDfci = {
  dfciTravauxDestinataireEmail: string;
  dfciTravauxObjetEmail: string;
  dfciTravauxCorpsEmail: string;
  dfciToleranceDfciPisteMetres: number;
  dfciListeCouche: DFCI_LISTE_COUCHE[];
};

type ParametresSectionMobile = {
  affichageIndispo: boolean;
  modeDeconnecte: boolean;
  creationPeiMobile: boolean;
  affichageSymbolesNormalises: boolean;
  caracteristiquesPena: [];
  caracteristiquesPibi: [];
  caracteristiquesPibiIds: { id: string }[];
  caracteristiquesPenaIds: { id: string }[];
  dureeValiditeToken: number;
  gestionAgent: string;
  bridagePhoto: boolean;
};

type ParametresSectionCartographie = {
  coordonneesFormatAffichage: string;
  empriseNative: string | undefined;
  rechercheBan: boolean;
  listeToponymieCode: string[];
  banCodeDepartement: string;
};

type ParametresSectionCouvertureHydraulique = {
  deciDistanceMaxParcours: number;
  deciIsodistances: string;
  profondeurCouverture: number;
};

type ParametresSectionPermis = {
  permisToleranceChargementMetres: number;
};

type ParametresSectionPei = {
  peiColonnes: string[] | undefined;
  peiColonnesIds: { id: string; libelle: string }[] | undefined;
  peiNombreHistorique: number | undefined;
  bufferCarte: number;
  caracteristiquesPenaTooltipWeb: string[] | undefined;
  caracteristiquesPibiTooltipWeb: string[] | undefined;
  caracteristiquesPenaTooltipWebIds: { id: string }[];
  caracteristiquesPibiTooltipWebIds: { id: string }[];
  peiFicheResumeStandalone: boolean;
  peiDisplayIdentifiantGestionnaire: boolean;
  peiDisplayTypeEngin: boolean;
  peiGenerationCarteTournee: boolean;
  peiMethodeTriAlphanumerique: boolean;
  peiRenumerotationInterneAuto: boolean;
  voieSaisieLibre: boolean;
  declarationPeiDestinataireEmail: string;
  declarationPeiObjetEmail: string;
  declarationPeiCorpsEmail: string;
  receptionRecoInitObligatoire: boolean;
  autoriserMailVisiteReception: boolean;
  autoriserMailRoi: boolean;
  peiRouteHistorique: string;
  valeurMinimaleHistogramme: number | undefined;
  conserverObservationVisite: boolean;
  peiOrganismeNotificationVisiteReception: string[] | undefined;
  peiOrganismeNotificationRoi: string[] | undefined;
};

type ParametresSectionPeiLongueIndispo = {
  peiLongueIndisponibiliteMessage: string;
  peiLongueIndisponibiliteJours: number;
  peiLongueIndisponibiliteTypeOrganisme: [];
};

type ParametresSectionRcci = {
  listeTypeOrganismeDdtmOnf: [];
  listeTypeOrganismeSdis: [];
  listeTypeOrganismeGendarmerie: [];
  listeTypeOrganismePolice: [];
};

type ParametresSectionUtilisateur = {
  organismeDefaut: string;
  profilUtilisateurDefaut: string;
  organismeProfilMajSynchro: boolean;
};

type ParametresSectionCourrier = {
  courrierRestrictionZC: boolean;
};

type AdminParametresValue = {
  general: ParametresSectionGeneral;
  signalement: ParametresSectionSignalement;
  mobile: ParametresSectionMobile;
  cartographie: ParametresSectionCartographie;
  couvertureHydraulique: ParametresSectionCouvertureHydraulique;
  dfci: ParametresSectionDfci;
  permis: ParametresSectionPermis;
  pei: ParametresSectionPei;
  peiLongueIndispo: ParametresSectionPeiLongueIndispo;
  rcci: ParametresSectionRcci;
  utilisateur: ParametresSectionUtilisateur;
  courrier: ParametresSectionCourrier;
};

export const getInitialValues = (
  data: AdminParametresValue,
): {
  general: ParametresSectionGeneral;
  signalement: ParametresSectionSignalement;
  mobile: ParametresSectionMobile;
  cartographie: ParametresSectionCartographie;
  couvertureHydraulique: ParametresSectionCouvertureHydraulique;
  dfci: ParametresSectionDfci;
  permis: ParametresSectionPermis;
  pei: ParametresSectionPei;
  peiLongueIndispo: ParametresSectionPeiLongueIndispo;
  rcci: ParametresSectionRcci;
  utilisateur: ParametresSectionUtilisateur;
  courrier: ParametresSectionCourrier;
} => ({
  general: data?.general,
  signalement: data?.signalement,
  mobile: {
    ...data?.mobile,
    caracteristiquesPibiIds:
      data?.mobile?.caracteristiquesPibi?.map((e) => ({
        id: e,
      })) ?? [],
    caracteristiquesPenaIds:
      data?.mobile?.caracteristiquesPena?.map((e) => ({
        id: e,
      })) ?? [],
  },
  cartographie: data?.cartographie,
  couvertureHydraulique: {
    ...data?.couvertureHydraulique,
    deciIsodistances: Array.isArray(
      data?.couvertureHydraulique?.deciIsodistances,
    )
      ? data.couvertureHydraulique.deciIsodistances.join(",")
      : typeof data?.couvertureHydraulique?.deciIsodistances === "string"
        ? data.couvertureHydraulique.deciIsodistances
        : "",
  },
  dfci: data?.dfci,
  permis: data?.permis,
  pei: {
    ...data?.pei,
    peiColonnesIds: data?.pei?.peiColonnes?.map((e) => ({ id: e, libelle: e })),
    caracteristiquesPibiTooltipWebIds:
      data?.pei?.caracteristiquesPibiTooltipWeb?.map((e) => ({
        id: e,
      })) ?? [],
    caracteristiquesPenaTooltipWebIds:
      data?.pei?.caracteristiquesPenaTooltipWeb?.map((e) => ({
        id: e,
      })) ?? [],
  },
  peiLongueIndispo: data?.peiLongueIndispo,
  rcci: data?.rcci,
  utilisateur: data?.utilisateur,
  courrier: data?.courrier,
});

export const validationSchema = object({
  general: object({
    titrePage: requiredString,
    toleranceVoiesMetres: requiredNumber,
  }),
  signalement: object({
    signalementDeliberationDestinataireEmail: requiredString,
    signalementDeliberationObjetEmail: requiredString,
    signalementDeliberationCorpsEmail: requiredString,
  }),
  cartographie: object({
    coordonneesFormatAffichage: requiredString,
  }),
  couvertureHydraulique: object({
    deciDistanceMaxParcours: requiredNumber,
    deciIsodistances: requiredString,
    profondeurCouverture: requiredNumber,
  }),
  dfci: object({
    dfciTravauxDestinataireEmail: requiredString,
    dfciTravauxObjetEmail: requiredString,
    dfciTravauxCorpsEmail: requiredString,
  }),
  permis: object({
    permisToleranceChargementMetres: requiredNumber,
  }),
  pei: object({
    peiHighlightDuree: requiredNumber,
    peiHighlightRayon: requiredNumber,
    peiHighlightLargeur: requiredNumber,
    peiSelectionRayon: requiredNumber,
    peiSelectionLargeur: requiredNumber,
    peiToleranceCommuneMetres: requiredNumber,
    peiRouteHistorique: requiredString,
    vitesseEau: requiredNumber,
    peiDelaiCtrlUrgent: requiredNumber,
    peiDelaiCtrlWarn: requiredNumber,
    peiDelaiRecoUrgent: requiredNumber,
    peiDelaiRecoWarn: requiredNumber,
    peiRenouvellementCtrlPrive: requiredNumber,
    peiRenouvellementCtrlPublic: requiredNumber,
    peiRenouvellementCtrlConventionne: requiredNumber,
    peiRenouvellementCtrlIcpe: requiredNumber,
    peiRenouvellementCtrlIcpeConventionne: requiredNumber,
    peiRenouvellementRecoPrive: requiredNumber,
    peiRenouvellementRecoPublic: requiredNumber,
    peiRenouvellementRecoIcpe: requiredNumber,
    peiRenouvellementRecoIcpeConventionne: requiredNumber,
    peiRenouvellementRecoConventionne: requiredNumber,
    peiDeplacementDistWarn: requiredNumber,
    bufferCarte: requiredNumber,
    declarationPeiDestinataireEmail: requiredString,
    declarationPeiObjetEmail: requiredString,
    declarationPeiCorpsEmail: requiredString,
  }),
  peiLongueIndispo: object({
    peiLongueIndisponibiliteMessage: requiredString,
    peiLongueIndisponibiliteJours: requiredNumber,
  }),
  mobile: object({
    dureeValiditeToken: requiredNumber,
  }),
  courrier: object({
    courrierRestrictionZC: requiredBoolean,
  }),
});

export const prepareVariables = (values: AdminParametresValue) => {
  return {
    general: values?.general,
    signalement: values?.signalement,
    mobile: {
      ...values?.mobile,
      caracteristiquesPena:
        values?.mobile?.caracteristiquesPenaIds?.map((e) => e.id) ?? [],
      caracteristiquesPibi:
        values?.mobile?.caracteristiquesPibiIds?.map((e) => e.id) ?? [],
    },
    cartographie: values?.cartographie,
    couvertureHydraulique: {
      ...values?.couvertureHydraulique,
      deciIsodistances:
        values?.couvertureHydraulique?.deciIsodistances?.toString(),
    },
    dfci: values?.dfci,
    permis: values?.permis,
    pei: {
      ...values?.pei,
      peiColonnes: values?.pei?.peiColonnesIds?.map((e) => e.id) ?? [],
      caracteristiquesPenaTooltipWeb:
        values?.pei?.caracteristiquesPenaTooltipWebIds?.map((e) => e.id) ?? [],
      caracteristiquesPibiTooltipWeb:
        values?.pei?.caracteristiquesPibiTooltipWebIds?.map((e) => e.id) ?? [],
    },
    peiLongueIndispo: values?.peiLongueIndispo,
    rcci: values?.rcci,
    utilisateur: values?.utilisateur,
    courrier: values?.courrier,
  };
};

/**
 * Permet de vérifier si le formulaire est conforme. S'il ne l'est pas, on ouvre la section qui doit être modifiée
 * et on scroll jusqu'au premier champ en erreur.
 */
function checkValidity(
  values: AdminParametresValue,
  show: (e: number) => void,
  listValuesWithConstraints: { name: string; accordionIndex: number }[],
) {
  let firstErrorField: string | null = null;

  listValuesWithConstraints.forEach((e) => {
    const value = getIn(values, e.name);

    if (
      value === null ||
      value === "" ||
      value === undefined ||
      (Array.isArray(value) && value.length === 0)
    ) {
      show(e.accordionIndex);
      if (!firstErrorField) {
        firstErrorField = e.name;
      }
    }
  });

  // Scroll vers le premier champ en erreur
  if (firstErrorField) {
    const fieldElement = document.querySelector(`[name="${firstErrorField}"]`);
    if (fieldElement) {
      fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }
}

const AdminParametres = () => {
  const adminParametresState = useGet(url`/api/admin/parametres`);
  const { data } = adminParametresState;

  return (
    <MyFormik
      initialValues={getInitialValues(data)}
      validationSchema={validationSchema}
      isPost={false}
      submitUrl={`/api/admin/parametres`}
      prepareVariables={(values) => prepareVariables(values)}
      onSubmit={() => null}
    >
      <AdminParametresInterne />
    </MyFormik>
  );
};

export const AdminParametresInterne = () => {
  const { values, setFieldValue } = useFormikContext<AdminParametresValue>();
  const allCaracteristiques = useGet(url`/api/admin/pei-caracteristique`)?.data;
  const { activesKeys, handleShowClose, show } = useAccordionState(
    Array(10).fill(false),
  );
  const { user } = useAppContext();

  // Liste des champs obligatoires avec leur index dans l'accordion
  // Permet d'ouvrir la section correspondante si un champ obligatoire n'est pas renseigné
  const listValuesWithConstraints: { name: string; accordionIndex: number }[] =
    [
      // Général (0)
      { name: "general.titrePage", accordionIndex: 0 },
      { name: "general.toleranceVoiesMetres", accordionIndex: 0 },
      // Signalement (1)
      {
        name: "signalement.signalementDeliberationDestinataireEmail",
        accordionIndex: 1,
      },
      {
        name: "signalement.signalementDeliberationObjetEmail",
        accordionIndex: 1,
      },
      {
        name: "signalement.signalementDeliberationCorpsEmail",
        accordionIndex: 1,
      },
      // Cartographie (2)
      { name: "cartographie.coordonneesFormatAffichage", accordionIndex: 2 },
      { name: "pei.peiHighlightDuree", accordionIndex: 2 },
      { name: "pei.peiHighlightRayon", accordionIndex: 2 },
      { name: "pei.peiHighlightLargeur", accordionIndex: 2 },
      { name: "pei.peiSelectionRayon", accordionIndex: 2 },
      { name: "pei.peiSelectionLargeur", accordionIndex: 2 },
      // Couverture hydraulique (3)
      {
        name: "couvertureHydraulique.deciDistanceMaxParcours",
        accordionIndex: 3,
      },
      { name: "couvertureHydraulique.deciIsodistances", accordionIndex: 3 },
      { name: "couvertureHydraulique.profondeurCouverture", accordionIndex: 3 },
      // DFCI (4)
      { name: "dfci.dfciTravauxDestinataireEmail", accordionIndex: 4 },
      { name: "dfci.dfciTravauxObjetEmail", accordionIndex: 4 },
      { name: "dfci.dfciTravauxCorpsEmail", accordionIndex: 4 },
      { name: "dfci.dfciToleranceDfciPisteMetres", accordionIndex: 4 },
      // Permis (5)
      { name: "permis.permisToleranceChargementMetres", accordionIndex: 5 },
      // PEI (6) - dans l'ordre d'affichage
      { name: "pei.peiColonnes", accordionIndex: 6 },
      { name: "pei.peiToleranceCommuneMetres", accordionIndex: 6 },
      { name: "pei.peiRouteHistorique", accordionIndex: 6 },
      { name: "pei.vitesseEau", accordionIndex: 6 },
      { name: "pei.peiDelaiCtrlUrgent", accordionIndex: 6 },
      { name: "pei.peiDelaiCtrlWarn", accordionIndex: 6 },
      { name: "pei.peiDelaiRecoUrgent", accordionIndex: 6 },
      { name: "pei.peiDelaiRecoWarn", accordionIndex: 6 },
      { name: "pei.peiRenouvellementCtrlPrive", accordionIndex: 6 },
      { name: "pei.peiRenouvellementCtrlPublic", accordionIndex: 6 },
      { name: "pei.peiRenouvellementCtrlConventionne", accordionIndex: 6 },
      { name: "pei.peiRenouvellementCtrlIcpe", accordionIndex: 6 },
      { name: "pei.peiRenouvellementCtrlIcpeConventionne", accordionIndex: 6 },
      { name: "pei.peiRenouvellementRecoPrive", accordionIndex: 6 },
      { name: "pei.peiRenouvellementRecoPublic", accordionIndex: 6 },
      { name: "pei.peiRenouvellementRecoIcpe", accordionIndex: 6 },
      { name: "pei.peiRenouvellementRecoIcpeConventionne", accordionIndex: 6 },
      { name: "pei.peiRenouvellementRecoConventionne", accordionIndex: 6 },
      { name: "pei.peiDeplacementDistWarn", accordionIndex: 6 },
      { name: "pei.bufferCarte", accordionIndex: 6 },
      { name: "pei.declarationPeiDestinataireEmail", accordionIndex: 6 },
      { name: "pei.declarationPeiObjetEmail", accordionIndex: 6 },
      { name: "pei.declarationPeiCorpsEmail", accordionIndex: 6 },
      ...(values?.pei?.autoriserMailVisiteReception
        ? [
            {
              name: "pei.peiOrganismeNotificationVisiteReception",
              accordionIndex: 6,
            },
          ]
        : []),
      ...(values?.pei?.autoriserMailRoi
        ? [
            {
              name: "pei.peiOrganismeNotificationRoi",
              accordionIndex: 6,
            },
          ]
        : []),
      // PEI longue indisponibilité (7)
      {
        name: "peiLongueIndispo.peiLongueIndisponibiliteMessage",
        accordionIndex: 7,
      },
      {
        name: "peiLongueIndispo.peiLongueIndisponibiliteJours",
        accordionIndex: 7,
      },
      {
        name: "peiLongueIndispo.peiLongueIndisponibiliteTypeOrganisme",
        accordionIndex: 7,
      },
      // Application mobile (9)
      { name: "mobile.dureeValiditeToken", accordionIndex: 9 },
      { name: "mobile.gestionAgent", accordionIndex: 9 },
    ];

  return (
    values && (
      <FormContainer>
        <Container>
          <PageTitle title="Paramètres applicatifs" icon={<IconParametre />} />

          <AccordionCustom
            activesKeys={activesKeys}
            list={[
              ...(hasDroit(user, TYPE_DROIT.ADMIN_PARAM_APPLI)
                ? [
                    {
                      header: "Général",
                      content: <AdminGeneral values={values.general} />,
                    },
                    {
                      header: "Signalement",
                      content: <AdminSignalement values={values.signalement} />,
                    },
                    {
                      header: "Cartographie",
                      content: (
                        <AdminCartographie values={values.cartographie} />
                      ),
                    },
                    {
                      header: "Couverture hydraulique",
                      content: (
                        <AdminCouvertureHydraulique
                          values={values.couvertureHydraulique}
                        />
                      ),
                    },
                    {
                      header: "DFCI",
                      content: <AdminDfci values={values.dfci} />,
                    },
                    {
                      header: "Permis",
                      content: <AdminPermis values={values.permis} />,
                    },
                    {
                      header: "PEI",
                      content: (
                        <AdminPei
                          values={values.pei}
                          allCaracteristiques={allCaracteristiques}
                        />
                      ),
                    },
                    {
                      header: "PEI longue indisponibilité",
                      content: (
                        <AdminPeiLongueIndispo
                          values={values.peiLongueIndispo}
                          setFieldValue={setFieldValue}
                        />
                      ),
                    },
                    {
                      header: "RCCI",
                      content: (
                        <AdminRcci
                          values={values.rcci}
                          setFieldValue={setFieldValue}
                        />
                      ),
                    },
                    {
                      header: "Utilisateur",
                      content: (
                        <AdminUtilisateur
                          values={values.utilisateur}
                          setFieldValue={setFieldValue}
                        />
                      ),
                    },
                  ]
                : []),
              ...(hasDroit(user, TYPE_DROIT.ADMIN_PARAM_APPLI_MOBILE)
                ? [
                    {
                      header: "Application mobile",
                      content: (
                        <AdminApplicationMobile
                          values={values.mobile}
                          allCaracteristiques={allCaracteristiques}
                        />
                      ),
                    },
                  ]
                : []),
              ...[
                {
                  header: "Courrier",
                  content: (
                    <AdminCourrier
                      values={values.courrier}
                      setFieldValue={setFieldValue}
                    />
                  ),
                },
              ],
            ]}
            handleShowClose={handleShowClose}
          />
          <br />
          <SubmitFormButtons
            returnLink={true}
            onClick={() => {
              checkValidity(values, show, listValuesWithConstraints);
            }}
          />
        </Container>
      </FormContainer>
    )
  );
};

export const AdminParametre = ({
  type = TYPE_PARAMETRE.STRING,
  children,
}: AdminParametreTypes) => {
  let bg = "primary";
  let text = "light";
  switch (type) {
    case TYPE_PARAMETRE.INTEGER:
      bg = "success";
      text = "dark";
      break;
    case TYPE_PARAMETRE.BOOLEAN:
      bg = "info";
      text = "light";
      break;
    case TYPE_PARAMETRE.BINARY:
      bg = "dark";
      text = "light";
      break;
    case TYPE_PARAMETRE.DOUBLE:
      bg = "warning";
      text = "dark";
      break;
    case TYPE_PARAMETRE.GEOMETRY:
      bg = "danger";
      text = "light";
      break;
    case TYPE_PARAMETRE.MULTI_STRING:
    case TYPE_PARAMETRE.MULTI_INT:
      bg = "secondary";
      text = "dark";
      break;
    case TYPE_PARAMETRE.SELECT:
      bg = "light";
      text = "dark";
      break;
  }
  return (
    <>
      <Row>
        <Col xs={2} className="text-end">
          <Badge bg={bg} text={text} pill className={"mt-3 ms-auto"}>
            {type}
          </Badge>
        </Col>
        <Col xs={10}>{children}</Col>
      </Row>
    </>
  );
};

type AdminParametreTypes = {
  type: ReactNode;
  children: ReactNode;
};

export default AdminParametres;

const AdminGeneral = ({ values }: { values: ParametresSectionGeneral }) => {
  return (
    values && (
      <>
        <AdminParametre type={TYPE_PARAMETRE.STRING}>
          <TextInput
            name="general.mentionCnil"
            label="Mention CNIL"
            tooltipText={
              "Mention CNIL affichée en bas de page (peut être vide)"
            }
            required={false}
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.STRING}>
          <TextInput
            name="general.messageEntete"
            label="Message d'entête"
            tooltipText={"Message affiché dans le cartouche d'entête"}
            required={false}
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.STRING}>
          <TextInput
            name="general.titrePage"
            label="Titre de la page"
            tooltipText={"Titre affiché dans l'entête du navigateur"}
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.INTEGER}>
          <PositiveNumberInput
            name="general.toleranceVoiesMetres"
            label="Tolérance (en m) de chargement des voies"
            tooltipText={
              "Utilisé dans le chargement des listes déroulantes des voies lorsque les géométries ne sont pas parfaitement en phase"
            }
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.STRING}>
          <TextAreaInput
            name="general.accueilPublic"
            required={false}
            label="Personnaliser la page d'accueil publique"
            tooltipText={
              "Écrivez du HTML pour personnaliser votre page d'accueil publique."
            }
          />
        </AdminParametre>
      </>
    )
  );
};

const AdminApplicationMobile = ({
  values,
  allCaracteristiques,
}: {
  values: ParametresSectionMobile;
  allCaracteristiques: any[];
}) => {
  const { setFieldValue } = useFormikContext();
  // Charger toutes les caractéristiques

  const {
    availableOptions: availablePibiOptions,
    setAvailableOptions: setAvailablePibiOptions,
    selectedOptions: selectedPibiOptions,
    setSelectedOptions: setSelectedPibiOptions,
  } = useTransferList({
    listeDisponible: null,
    listeSelectionne: null,
    nameFormik: "mobile.caracteristiquesPibiIds",
  });

  const {
    availableOptions: availablePenaOptions,
    setAvailableOptions: setAvailablePenaOptions,
    selectedOptions: selectedPenaOptions,
    setSelectedOptions: setSelectedPenaOptions,
  } = useTransferList({
    listeDisponible: null,
    listeSelectionne: null,
    nameFormik: "mobile.caracteristiquesPenaIds",
  });

  useMemo(() => {
    if (
      !allCaracteristiques ||
      !values?.caracteristiquesPibiIds ||
      !values?.caracteristiquesPenaIds
    ) {
      return;
    }
    //PIBI
    if (availablePibiOptions == null) {
      setAvailablePibiOptions(
        allCaracteristiques.filter(
          (item: any) => item.type === "PIBI" || item.type === "GENERAL",
        ) as any[],
      );
    }
    if (selectedPibiOptions == null) {
      setSelectedPibiOptions(
        allCaracteristiques.filter((item: any) => {
          return values?.caracteristiquesPibiIds
            ?.map((e) => e.id)
            .includes(item.id);
        }),
      );
    }
    //PENA
    if (availablePenaOptions == null) {
      setAvailablePenaOptions(
        allCaracteristiques.filter(
          (item: any) => item.type === "PENA" || item.type === "GENERAL",
        ),
      );
    }
    if (selectedPenaOptions == null) {
      setSelectedPenaOptions(
        allCaracteristiques.filter((item: any) => {
          return values?.caracteristiquesPenaIds
            ?.map((e) => e.id)
            .includes(item.id);
        }),
      );
    }
  }, [
    values,
    availablePenaOptions,
    availablePibiOptions,
    selectedPenaOptions,
    selectedPibiOptions,
    setAvailablePenaOptions,
    setAvailablePibiOptions,
    setSelectedPenaOptions,
    setSelectedPibiOptions,
    allCaracteristiques,
  ]);

  return (
    values && (
      <>
        <AdminParametre type={TYPE_PARAMETRE.BOOLEAN}>
          <CheckBoxInput
            name="mobile.affichageIndispo"
            label="Afficher l'état de disponibilité du PEI"
            checked={values?.affichageIndispo}
            tooltipText={
              "Ajout d'une croix rouge sur le symbole du PEI pour signifier l'indisponibilité"
            }
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.BOOLEAN}>
          <CheckBoxInput
            name="mobile.affichageSymbolesNormalises"
            label="Utiliser la symbologie du RNDECI"
            checked={values?.affichageSymbolesNormalises}
            tooltipText={
              "Utiliser la symbologie définie dans le RNDECI plutôt que des cercles"
            }
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.MULTI_STRING}>
          <TransferList
            availableOptions={availablePibiOptions}
            selectedOptions={selectedPibiOptions}
            setAvailableOptions={setAvailablePibiOptions}
            setSelectedOptions={setSelectedPibiOptions}
            label={"Caractéristiques à afficher pour les PIBI"}
            tooltipText={
              "Dans l'infobulle d'un PEI sur l'application mobile, " +
              "on affiche des caractéristiques. Ce paramètre permet de définir lesquelles, " +
              "et dans quel ordre elles se présentent"
            }
            required={false}
            name={"mobile.caracteristiquePibi"}
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.MULTI_STRING}>
          <TransferList
            availableOptions={availablePenaOptions}
            selectedOptions={selectedPenaOptions}
            setAvailableOptions={setAvailablePenaOptions}
            setSelectedOptions={setSelectedPenaOptions}
            label={"Caractéristiques à afficher pour les PENA"}
            tooltipText={
              "Dans l'infobulle d'un PEI sur l'application mobile, " +
              "on affiche des caractéristiques. Ce paramètre permet de définir lesquelles, " +
              "et dans quel ordre elles se présentent"
            }
            required={false}
            name={"mobile.caracteristiquePena"}
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.INTEGER}>
          <PositiveNumberInput
            name="mobile.dureeValiditeToken"
            label="Durée de validité du token"
            tooltipText={
              "Durée (en heures) de validité du jeton de connexion à " +
              "l'application mobile."
            }
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.SELECT}>
          <SelectInput
            name="mobile.gestionAgent"
            label="Type d'agent sélectionné"
            options={typeAgent}
            getOptionValue={(v) => v.id}
            getOptionLabel={(v) => v.libelle}
            onChange={(e) =>
              setFieldValue(
                `mobile.gestionAgent`,
                typeAgent.find((type) => type.id === e.id)?.id,
              )
            }
            defaultValue={typeAgent.find((e) => e.id === values.gestionAgent)}
          />
          <SeeMoreButton>
            <>
              <p className="m-2">
                C&apos;est un champ texte mixé avec une liste déroulante, comme
                par exemple les listes déroulantes de l&apos;accès rapide du
                module point d&apos;eau (application web remocra). Par défaut il
                est vide. L&apos;utilisateur saisit ce qu&apos;il veut ( Michel
                Dupont par exemple, ou toto@sdisXX.fr ), on va stocker cette
                valeur dans les propriétés de l&apos;application (commun à tous
                les utilisateurs de cette tablette) A la prochaine saisie,
                l&apos;utilisateur peut soit rajouter un autre agent, soit
                retrouver les valeurs précédemment saisies. La liste des agents
                disponibles pour le composant n&apos;est pas envoyée au serveur,
                c&apos;est un facilitateur de saisie ; la tablette enverra le
                résultat de la saisie pour chaque PEI.
                <br />
                Pour l&apos;instant les scénarios suivants ont été identifiés
              </p>
              <div className="h5">Cas 1 - Utilisateur connecté obligatoire</div>
              <p className="m-2">
                L&apos;agent 1 est toujours l&apos;utilisateur connecté, on
                préremplit donc le champ avec ses informations. On rend le champ
                inaccessible pour éviter une modification manuelle
              </p>
              <p className="m-2">L&apos;agent 2 utilise le composant Agent</p>

              <div className="h5">Cas 2 - Utilisateur connecté</div>
              <p className="m-2">
                L&apos;agent 1 est toujours l&apos;utilisateur connecté, on
                préremplit donc le champ avec ses informations. On laisse
                possible la saisie manuelle
              </p>
              <p className="m-2">L&apos;agent 2 utilise le composant Agent</p>

              <div className="h5">Cas 3 - Liste des agents</div>
              <p className="m-2">
                Les 2 champs de formulaire sont vides par défaut, et on propose
                l&apos;utilisation du composant Agent pour les 2
              </p>

              <div className="h5">Cas 4 - Valeur précédente</div>
              <p className="m-2">
                Idem cas 2, mais les valeurs par défaut correspondent à la
                valeur précédemment sélectionnée
              </p>
            </>
          </SeeMoreButton>
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.STRING}>
          <TextInput
            name="mobile.mdpAdministrateur"
            label="Mot de passe administrateur"
            tooltipText={
              "Mot de passe permettant de déverrouiller l'administration de l'appli mobile, à savoir la configuration du serveur ; inutile lors d'un paramétrage via MDM"
            }
            password
            required={false}
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.BOOLEAN}>
          <CheckBoxInput
            name="mobile.modeDeconnecte"
            label="Activer le mode déconnecté"
            checked={values?.modeDeconnecte}
            tooltipText={
              "Si la couverture Internet du département n'est pas satisfaisante, vous pouvez passer par le 'mode déconnecté' pour permettre une connexion d'un utilisateur sur une longue durée."
            }
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.BOOLEAN}>
          <CheckBoxInput
            name="mobile.creationPeiMobile"
            label="Autoriser la création de PEI"
            checked={values?.creationPeiMobile}
            tooltipText={
              "Active ou non la fonctionnalité de création de PEI depuis l'application mobile ; si activée, l'utilisateur devra toujours posséder le droit adéquat."
            }
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.BOOLEAN}>
          <CheckBoxInput
            name="mobile.bridagePhoto"
            label="Brider la taille des photos"
            checked={values?.bridagePhoto}
            tooltipText={
              "Les photos prises par l'application mobile peut être de taille importante, et en fonction de la volumétrie des PEI des tournées, on peut avoir des problèmes de synchronisation. Réduire à environ 1920*1080px les photos limite drastiquement le poids de celles-ci, donc les problèmes liés lors de la synchronisation."
            }
          />
        </AdminParametre>
      </>
    )
  );
};

const AdminSignalement = ({
  values,
}: {
  values: ParametresSectionSignalement;
}) => {
  return (
    values && (
      <>
        <AdminParametre type={TYPE_PARAMETRE.STRING}>
          <TextAreaInput
            name="signalement.signalementDeliberationDestinataireEmail"
            label="Adresse email qui recevra une notification lors du dépôt d'une délibération"
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.STRING}>
          <TextAreaInput
            name="signalement.signalementDeliberationObjetEmail"
            label="Objet du mail de notification lors du dépôt d'une délibération"
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.STRING}>
          <TextAreaInput
            name="signalement.signalementDeliberationCorpsEmail"
            label="Contenu du mail de notification lors du dépôt d'une délibération"
            tooltipText="Vous pouvez utiliser #[ORGANISME_UTILISATEUR]# et #[LIEN_TELECHARGEMENT]# dans votre message. Ces deux valeurs seront remplacées automatiquement."
          />
        </AdminParametre>
      </>
    )
  );
};

const AdminCartographie = ({
  values,
}: {
  values: ParametresSectionCartographie;
}) => {
  const { setFieldValue } = useFormikContext();

  const { data: zoneCompetence } = useGet(
    url`/api/zone-integration/get-active`,
  );

  const toponymieList = useGet(url`/api/toponymie/get-libelle-toponymie`);

  return (
    values && (
      <>
        <AdminParametre type={TYPE_PARAMETRE.SELECT}>
          <SelectInput
            name="cartographie.coordonneesFormatAffichage"
            label="Format d'affichage des coordonnées"
            options={typeAffichageCoordonnees}
            getOptionValue={(v) => v.id}
            getOptionLabel={(v) => v.libelle}
            onChange={(e) =>
              setFieldValue(
                `cartographie.coordonneesFormatAffichage`,
                typeAffichageCoordonnees.find((type) => type.id === e.id)?.id,
              )
            }
            defaultValue={typeAffichageCoordonnees.find(
              (e) => e.id === values.coordonneesFormatAffichage,
            )}
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.SELECT}>
          <SelectInput
            name="cartographie.empriseNative"
            label="Emprise par défaut de la carte"
            options={zoneCompetence}
            getOptionValue={(v) => v.code}
            getOptionLabel={(v) => v.libelle}
            required={false}
            tooltipText={
              "Emprise géographique affichée par défaut sur la carte lorsque l'utilisateur n'est pas connecté ou lorsqu'il n'a pas de zone de compétence définie (cas des super administrateurs)"
            }
            onChange={(e) =>
              setFieldValue(
                `cartographie.empriseNative`,
                zoneCompetence?.find((zc: IdCodeLibelleType) => zc.id === e.id)
                  ?.code,
              )
            }
            defaultValue={zoneCompetence?.find(
              (e: IdCodeLibelleType) => e.code === values.empriseNative,
            )}
          />
        </AdminParametre>

        <AdminParametre type={TYPE_PARAMETRE.INTEGER}>
          <PositiveNumberInput
            name="pei.peiHighlightDuree"
            label="Durée (en secondes) de mise en surbrillance des PEI"
            tooltipText={
              "Lors d'une localisation de PEI depuis une source extérieure (PEI, IT, ...), on met en évidence brièvement les PEI concernés"
            }
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.STRING}>
          <ColorInput
            name="pei.peiHighlightCouleur"
            label="Couleur de mise en surbrillance des PEI"
            tooltipText={
              "Couleur du cercle de surbrillance des PEI lors d'une localisation de PEI depuis une source extérieure (PEI, IT, ...)"
            }
            required={false}
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.INTEGER}>
          <PositiveNumberInput
            name="pei.peiHighlightRayon"
            label="Rayon du cercle de surbrillance des PEI (en pixels)"
            tooltipText={
              "Rayon du cercle de surbrillance des PEI lors d'une localisation de PEI depuis une source extérieure (PEI, IT, ...)"
            }
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.INTEGER}>
          <PositiveNumberInput
            name="pei.peiHighlightLargeur"
            label="Largeur du trait de surbrillance des PEI (en pixels)"
            tooltipText={
              "Largeur du trait du cercle de surbrillance des PEI lors d'une localisation de PEI depuis une source extérieure (PEI, IT, ...)"
            }
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.STRING}>
          <ColorInput
            name="pei.peiSelectionCouleur"
            label="Couleur de sélection des PEI"
            tooltipText={
              "Couleur du cercle de sélection des PEI lors de la sélection sur la carte (Ctrl+clic ou sélection par zone)"
            }
            required={false}
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.INTEGER}>
          <PositiveNumberInput
            name="pei.peiSelectionRayon"
            label="Rayon du cercle de sélection des PEI (en pixels)"
            tooltipText={
              "Rayon du cercle de sélection des PEI lors de la sélection sur la carte (Ctrl+clic ou sélection par zone)"
            }
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.INTEGER}>
          <PositiveNumberInput
            name="pei.peiSelectionLargeur"
            label="Largeur du trait de sélection des PEI (en pixels)"
            tooltipText={
              "Largeur du trait du cercle de sélection des PEI lors de la sélection sur la carte (Ctrl+clic ou sélection par zone)"
            }
          />
        </AdminParametre>

        <AdminParametre type={TYPE_PARAMETRE.BOOLEAN}>
          <CheckBoxInput
            name="cartographie.rechercheBan"
            checked={values.rechercheBan}
            label="Rechercher par BAN"
            tooltipText={
              "Permet de choisir entre une recherche par BAN ou une recherche parmi les voies et autres données disponibles dans la base de données de l'application."
            }
          />
        </AdminParametre>
        {values.rechercheBan && (
          <AdminParametre type={TYPE_PARAMETRE.STRING}>
            <TextInput
              name="cartographie.banCodeDepartement"
              label="Code département pour la recherche BAN"
              tooltipText={
                "La recherche BAN sera limitée aux départements spécifiés séparés par des virgules (ex: 89, 21). Si ce champ est laissé vide, la recherche BAN s'effectuera sur l'ensemble du territoire français."
              }
              required={false}
            />
          </AdminParametre>
        )}

        {!values.rechercheBan && (
          <>
            <AdminParametre type={TYPE_PARAMETRE.MULTI_STRING}>
              <Multiselect
                name={"cartographie.listeToponymieCode"}
                label="Répertoire des lieux de recherche"
                options={toponymieList.data?.filter(
                  (item: { code: string }) =>
                    !["COMMUNE", "ROUTES"].includes(item.code),
                )}
                getOptionValue={(t) => t.code}
                getOptionLabel={(t) => t.libelle}
                isClearable={true}
                required={false}
                value={
                  values.listeToponymieCode?.map((e: string) =>
                    toponymieList.data?.find(
                      (c: IdCodeLibelleType) => c.code === e,
                    ),
                  ) ?? undefined
                }
                onChange={(toponymies) => {
                  const toponymiesCode = toponymies.map(
                    (e: { code: string }) => e.code,
                  );
                  toponymiesCode.length > 0
                    ? setFieldValue(
                        "cartographie.listeToponymieCode",
                        toponymiesCode,
                      )
                    : setFieldValue("cartographie.listeToponymieCode", []);
                }}
              />
            </AdminParametre>
          </>
        )}
      </>
    )
  );
};

const AdminCouvertureHydraulique = ({
  values,
}: {
  values: ParametresSectionCouvertureHydraulique;
}) => {
  return (
    values && (
      <>
        <AdminParametre type={TYPE_PARAMETRE.INTEGER}>
          <PositiveNumberInput
            name="couvertureHydraulique.deciDistanceMaxParcours"
            label="Distance max de parcours (en m)"
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.MULTI_INT}>
          {/*Séparation par des virgule vérifié en backend*/}
          <TextInput
            name="couvertureHydraulique.deciIsodistances"
            label="Isodistances à prendre en compte (en m)"
            tooltipText={"Plusieurs valeurs séparées par des virgules"}
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.INTEGER}>
          <PositiveNumberInput
            name="couvertureHydraulique.profondeurCouverture"
            label="Profondeur de couverture (en m)"
          />
        </AdminParametre>
      </>
    )
  );
};

const AdminDfci = ({ values }: { values: ParametresSectionDfci }) => {
  const { setFieldValue } = useFormikContext();

  const listeCoucheDfci: IdCodeLibelleType[] = referenceDfciListeCouche.map(
    (e) => ({
      id: e.code,
      code: e.code,
      libelle: e.libelle,
    }),
  );

  return (
    values && (
      <>
        <AdminParametre type={TYPE_PARAMETRE.STRING}>
          <TextAreaInput
            name="dfci.dfciTravauxDestinataireEmail"
            label="Adresse email qui recevra une notification lors d'une réception de travaux"
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.STRING}>
          <TextAreaInput
            name="dfci.dfciTravauxObjetEmail"
            label="Objet du mail de notification lors d'une réception de travaux"
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.STRING}>
          <TextAreaInput
            name="dfci.dfciTravauxCorpsEmail"
            label="Contenu du mail de notification lors d'une réception de travaux"
            tooltipText="Vous pouvez utiliser #[ORGANISME_UTILISATEUR]# et #[LIEN_TELECHARGEMENT]# dans votre message. Ces deux valeurs seront remplacées automatiquement."
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.INTEGER}>
          <PositiveNumberInput
            name="dfci.dfciToleranceDfciPisteMetres"
            label="Tolérance (en m) de détection des pistes"
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.MULTI_STRING}>
          <Multiselect
            name={"dfci.dfciListeCouche"}
            label="Liste des couches DFCI à afficher"
            options={listeCoucheDfci}
            getOptionValue={(e) => e.id}
            getOptionLabel={(e) => e.libelle}
            value={
              values?.dfciListeCouche?.map((e) =>
                listeCoucheDfci?.find((r: IdCodeLibelleType) => r.id === e),
              ) ?? undefined
            }
            onChange={(coucheDfci) => {
              const coucheDfciListe: DFCI_LISTE_COUCHE[] = coucheDfci.map(
                (e) => e.id,
              );
              coucheDfciListe.length > 0
                ? setFieldValue("dfci.dfciListeCouche", coucheDfciListe)
                : setFieldValue("dfci.dfciListeCouche", []);
            }}
            required={false}
          />
        </AdminParametre>
      </>
    )
  );
};

const AdminPei = ({
  values,
  allCaracteristiques,
}: {
  values: ParametresSectionPei;
  allCaracteristiques: any[];
}) => {
  const {
    availableOptions,
    setAvailableOptions,
    selectedOptions,
    setSelectedOptions,
  } = useTransferList({
    listeDisponible: null,
    listeSelectionne: null,
    nameFormik: "pei.peiColonnesIds",
  });
  const { setFieldValue } = useFormikContext();

  useMemo(() => {
    if (!values?.peiColonnesIds) {
      return;
    }
    if (availableOptions == null) {
      setAvailableOptions(
        referenceColumnPei.filter((e) => !values.peiColonnesIds?.includes(e)),
      );
    }
    if (selectedOptions == null) {
      setSelectedOptions(
        referenceColumnPei.filter(({ id }) =>
          values.peiColonnesIds?.map((e) => e.id).includes(id),
        ),
      );
    }
  }, [
    values,
    setSelectedOptions,
    selectedOptions,
    availableOptions,
    setAvailableOptions,
  ]);

  const {
    availableOptions: availablePibiOptions,
    setAvailableOptions: setAvailablePibiOptions,
    selectedOptions: selectedPibiOptions,
    setSelectedOptions: setSelectedPibiOptions,
  } = useTransferList({
    listeDisponible: null,
    listeSelectionne: null,
    nameFormik: "pei.caracteristiquesPibiTooltipWebIds",
  });

  const {
    availableOptions: availablePenaOptions,
    setAvailableOptions: setAvailablePenaOptions,
    selectedOptions: selectedPenaOptions,
    setSelectedOptions: setSelectedPenaOptions,
  } = useTransferList({
    listeDisponible: null,
    listeSelectionne: null,
    nameFormik: "pei.caracteristiquesPenaTooltipWebIds",
  });

  useMemo(() => {
    if (
      !allCaracteristiques ||
      !values?.caracteristiquesPenaTooltipWebIds ||
      !values?.caracteristiquesPibiTooltipWebIds
    ) {
      return;
    }
    //PIBI
    if (availablePibiOptions == null) {
      setAvailablePibiOptions(
        allCaracteristiques.filter(
          (item: any) => item.type === "PIBI" || item.type === "GENERAL",
        ),
      );
    }
    if (selectedPibiOptions == null) {
      setSelectedPibiOptions(
        allCaracteristiques.filter((item: any) => {
          return values?.caracteristiquesPibiTooltipWebIds
            ?.map((e) => e.id)
            .includes(item.id);
        }),
      );
    }
    //PENA
    if (availablePenaOptions == null) {
      setAvailablePenaOptions(
        allCaracteristiques.filter(
          (item: any) => item.type === "PENA" || item.type === "GENERAL",
        ),
      );
    }
    if (selectedPenaOptions == null) {
      setSelectedPenaOptions(
        allCaracteristiques.filter((item: any) => {
          return values?.caracteristiquesPenaTooltipWebIds
            ?.map((e) => e.id)
            .includes(item.id);
        }),
      );
    }
  }, [
    values,
    availablePenaOptions,
    availablePibiOptions,
    selectedPenaOptions,
    selectedPibiOptions,
    setAvailablePenaOptions,
    setAvailablePibiOptions,
    setSelectedPenaOptions,
    setSelectedPibiOptions,
    allCaracteristiques,
  ]);

  return (
    values && (
      <>
        <h2 className="mt-2 ms-5">Liste PEI</h2>
        <AdminParametre type={TYPE_PARAMETRE.MULTI_STRING}>
          <TransferList
            availableOptions={availableOptions}
            selectedOptions={selectedOptions}
            setAvailableOptions={setAvailableOptions}
            setSelectedOptions={setSelectedOptions}
            required={true}
            label={"Colonnes affichées dans la liste des PEI"}
            name={"pei.peiColonnes"}
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.BOOLEAN}>
          <CheckBoxInput
            name="pei.peiMethodeTriAlphanumerique"
            label="Activer le tri alphanumérique pour les PEI"
            checked={values?.peiMethodeTriAlphanumerique}
            tooltipText={
              "Un tri alphanumérique compare un à un les caractères de chaque chaîne, alors qu'un tri 'naturel' va d'abord comparer la longueur de chaque chaîne"
            }
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.INTEGER}>
          <PositiveNumberInput
            name="pei.peiToleranceCommuneMetres"
            label="Tolérance en mètres pour les communes"
            tooltipText={
              "Permet de charger dans la liste déroulante des communes certaines dont le polygone n'est pas parfaitement en phase avec l'attendu"
            }
          />
        </AdminParametre>

        <h2 className="mt-4 ms-5">Infobulle PEI</h2>
        <AdminParametre type={TYPE_PARAMETRE.MULTI_STRING}>
          <TransferList
            availableOptions={availablePibiOptions}
            selectedOptions={selectedPibiOptions}
            setAvailableOptions={setAvailablePibiOptions}
            setSelectedOptions={setSelectedPibiOptions}
            label={"Caractéristiques à afficher pour les PIBI"}
            tooltipText={
              "Dans l'infobulle d'un PEI sur la carte de l'application web, " +
              "on affiche des caractéristiques. Ce paramètre permet de définir lesquelles, " +
              "et dans quel ordre elles se présentent"
            }
            required={false}
            name={"pei.caracteristiquesPibiTooltipWebIds"}
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.MULTI_STRING}>
          <TransferList
            availableOptions={availablePenaOptions}
            selectedOptions={selectedPenaOptions}
            setAvailableOptions={setAvailablePenaOptions}
            setSelectedOptions={setSelectedPenaOptions}
            label={"Caractéristiques à afficher pour les PENA"}
            tooltipText={
              "Dans l'infobulle d'un PEI sur la carte de l'application web, " +
              "on affiche des caractéristiques. Ce paramètre permet de définir lesquelles, " +
              "et dans quel ordre elles se présentent"
            }
            required={false}
            name={"pei.caracteristiquesPenaTooltipWebIds"}
          />
        </AdminParametre>

        <h2 className="mt-4 ms-5">Fiche Résumé</h2>
        <AdminParametre type={TYPE_PARAMETRE.BOOLEAN}>
          <CheckBoxInput
            name="pei.peiFicheResumeStandalone"
            label="Activer la fiche Résumé en affichage autonome"
            tooltipText={
              "La fiche Résumé permet d'afficher de l'information condensée sur le PEI ; outre son accessibilité en 1er accordéon de la fiche PEI, vous pouvez, en activant cette option, la voir apparaître comme action spécifique dans la liste des PEI et l'infobulle du PEI dans la carto. Attention, elle est forcément utilisée pour le module de risque en mode grand public"
            }
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.STRING}>
          <TextInput
            name="pei.peiLibelleNonConforme"
            label='Libellé du statut "non conforme" d&apos;un PEI'
            tooltipText={
              'Libellé qui sera affiché pour le statut "Non conforme" d\'un PEI ; par défaut, la valeur est "Non conforme". Attention, une valeur trop longue pourrait dégrader certains affichages.'
            }
            required={false}
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.INTEGER}>
          <PositiveNumberInput
            name="pei.peiNombreHistorique"
            label="Nombre de données à afficher sur le graphique de la fiche résumé"
            required={false}
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.INTEGER}>
          <PositiveNumberInput
            name="pei.valeurMinimaleHistogramme"
            label="Graduation minimale de l'histogramme de la Fiche Résumé"
            required={false}
            tooltipText="Saisir une valeur dans ce champ permet de garantir que l'ordonnée de l'histogramme ira au moins jusqu'à la valeur en question, mais pourra s'étendre au besoin."
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.SELECT}>
          <SelectInput
            name="pei.peiRouteHistorique"
            label="Type de route pour l'historique des PEI"
            options={typeRouteHistoriquePei}
            getOptionValue={(v) => v.id}
            getOptionLabel={(v) => v.libelle}
            onChange={(e) =>
              setFieldValue(
                `pei.peiRouteHistorique`,
                typeRouteHistoriquePei.find((type) => type.id === e.id)?.id,
              )
            }
            defaultValue={typeRouteHistoriquePei.find(
              (e) => e.id === values.peiRouteHistorique,
            )}
            tooltipText={
              "Le bouton pourra rediriger soit vers l'écran d'historique des opérations, en s'appuyant sur l'identifiant technique du PEI, soit vers le rapport personnalisé d'historique d'un PEI avec son numéro complet"
            }
          />
        </AdminParametre>

        <h2 className="mt-4 ms-5">Fiche PEI</h2>
        <AdminParametre type={TYPE_PARAMETRE.BOOLEAN}>
          <CheckBoxInput
            name="pei.peiRenumerotationInterneAuto"
            label="Activer la renumérotation interne automatique des PEI"
            checked={values?.peiRenumerotationInterneAuto}
            tooltipText={
              "Si le paramètre est activé, la saisie du numéro interne d'un PEI sera impossible même si l'utilisateur a le droit 'Saisir le numéro interne d'un PEI' (PEI_NUMERO_INTERNE_U)."
            }
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.BOOLEAN}>
          <CheckBoxInput
            name="pei.peiDisplayIdentifiantGestionnaire"
            label="Permettre la saisie du numéro utilisé par le gestionnaire pour identifier le PIBI."
            tooltipText={
              "Permet d'afficher le composant de saisie de l'identifiant gestionnaire dans la fiche PEI, sinon il sera masqué"
            }
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.BOOLEAN}>
          <CheckBoxInput
            name="pei.voieSaisieLibre"
            label="Autoriser la saisie libre pour les voies"
            checked={values?.voieSaisieLibre}
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.BOOLEAN}>
          <CheckBoxInput
            name="pei.peiDisplayTypeEngin"
            label="Permettre la saisie des types d'engins dans la fiche PEI"
            tooltipText={
              "Permet d'afficher le composant de saisie des types d'engins dans la fiche PEI, sinon il sera masqué"
            }
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.INTEGER}>
          <PositiveNumberInput
            name="pei.vitesseEau"
            label="Vitesse de l'eau en m/s"
            tooltipText={"Utile pour avoir un calcul fin des débits simultanés"}
          />
        </AdminParametre>

        <h2 className="mt-4 ms-5">Visites PEI</h2>
        <AdminParametre type={TYPE_PARAMETRE.BOOLEAN}>
          <CheckBoxInput
            name="pei.receptionRecoInitObligatoire"
            label="Obliger la visite de réception et la visite de reconnaissance initiale"
            checked={values?.receptionRecoInitObligatoire}
            tooltipText={
              <>
                Si le paramètre est coché, l&apos;absence de visite de réception
                et de visite de reconnaissance initiale entraîne obligatoirement
                une indisponibilité du PEI.
                <br />
                <br />
                Si ce paramètre change, une tâche de recalcul des disponibilités
                sera lancée.
                <br />
                Notez que cette tâche peut être longue en fonction du nombre de
                PEI à traiter Vous pouvez consulter la page{" "}
                <b>Résultats d&apos;exécution</b> pour suivre son avancement.
              </>
            }
          />
          {values?.receptionRecoInitObligatoire && (
            <>
              <AdminParametre type={TYPE_PARAMETRE.BOOLEAN}>
                <CheckBoxInput
                  name="pei.autoriserMailVisiteReception"
                  label="Notification - Visite de réception"
                  checked={values?.autoriserMailVisiteReception}
                />
              </AdminParametre>

              {values?.autoriserMailVisiteReception && (
                <ActiveOrganismTypes recipientsField="pei.peiOrganismeNotificationVisiteReception" />
              )}

              <AdminParametre type={TYPE_PARAMETRE.BOOLEAN}>
                <CheckBoxInput
                  name="pei.autoriserMailRoi"
                  label="Notification - Reconnaissance opérationnelle initiale (ROI)"
                  checked={values?.autoriserMailRoi}
                />
              </AdminParametre>

              {values?.autoriserMailRoi && (
                <ActiveOrganismTypes recipientsField="pei.peiOrganismeNotificationRoi" />
              )}
            </>
          )}
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.BOOLEAN}>
          <CheckBoxInput
            name="pei.conserverObservationVisite"
            label="Conserver la dernière observation lors de la saisie d'une nouvelle visite "
            checked={values?.conserverObservationVisite}
            tooltipText={
              <>
                Choisissez si vous souhaitez conserver l'observation précédente
                lors de la saisie d'une nouvelle visite, afin d'éviter de la
                saisir à nouveau.
              </>
            }
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.INTEGER}>
          <PositiveNumberInput
            name="pei.peiDelaiCtrlUrgent"
            label="Nombre de jours avant échéance où un contrôle est considéré comme urgent"
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.INTEGER}>
          <PositiveNumberInput
            name="pei.peiDelaiCtrlWarn"
            label="Nombre de jours avant échéance où un contrôle est considéré comme à faire bientôt"
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.INTEGER}>
          <PositiveNumberInput
            name="pei.peiDelaiRecoUrgent"
            label="Nombre de jours avant échéance où une reconnaisance est considérée comme urgente"
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.INTEGER}>
          <PositiveNumberInput
            name="pei.peiDelaiRecoWarn"
            label="Nombre de jours avant échéance où une reconnaisance est considérée comme à faire bientôt"
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.INTEGER}>
          <PositiveNumberInput
            name="pei.peiRenouvellementCtrlPrive"
            label="Nombre de jours pour le renouvellement des contrôles privés"
            tooltipText={
              "Permet de calculer la prochaine date du CTP par rapport à la dernière saisie"
            }
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.INTEGER}>
          <PositiveNumberInput
            name="pei.peiRenouvellementCtrlPublic"
            label="Nombre de jours pour le renouvellement des contrôles publics"
            tooltipText={
              "Permet de calculer la prochaine date du CTP par rapport à la dernière saisie"
            }
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.INTEGER}>
          <PositiveNumberInput
            name="pei.peiRenouvellementCtrlConventionne"
            label="Nombre de jours pour le renouvellement des contrôles Privés sous convention"
            tooltipText={
              "Permet de calculer la prochaine date du CTP par rapport à la dernière saisie"
            }
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.INTEGER}>
          <PositiveNumberInput
            name="pei.peiRenouvellementCtrlIcpe"
            label="Nombre de jours pour le renouvellement des contrôles ICPE"
            tooltipText={
              "Permet de calculer la prochaine date du CTP par rapport à la dernière saisie"
            }
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.INTEGER}>
          <PositiveNumberInput
            name="pei.peiRenouvellementCtrlIcpeConventionne"
            label="Nombre de jours pour le renouvellement des contrôles ICPE conventionnés"
            tooltipText={
              "Permet de calculer la prochaine date du CTP par rapport à la dernière saisie"
            }
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.INTEGER}>
          <PositiveNumberInput
            name="pei.peiRenouvellementRecoPrive"
            label="Nombre de jours pour le renouvellement des reconnaissances privées"
            tooltipText={
              "Permet de calculer la prochaine date de la ROP par rapport à la dernière saisie"
            }
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.INTEGER}>
          <PositiveNumberInput
            name="pei.peiRenouvellementRecoPublic"
            label="Nombre de jours pour le renouvellement des reconnaissances publiques"
            tooltipText={
              "Permet de calculer la prochaine date de la ROP par rapport à la dernière saisie"
            }
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.INTEGER}>
          <PositiveNumberInput
            name="pei.peiRenouvellementRecoIcpe"
            label="Nombre de jours pour le renouvellement des reconnaissances ICPE"
            tooltipText={
              "Permet de calculer la prochaine date de la ROP par rapport à la dernière saisie"
            }
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.INTEGER}>
          <PositiveNumberInput
            name="pei.peiRenouvellementRecoIcpeConventionne"
            label="Nombre de jours pour le renouvellement des reconnaissances ICPE conventionnés"
            tooltipText={
              "Permet de calculer la prochaine date de la ROP par rapport à la dernière saisie"
            }
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.INTEGER}>
          <PositiveNumberInput
            name="pei.peiRenouvellementRecoConventionne"
            label="Nombre de jours pour le renouvellement des reconnaissances Privés sous convention"
            tooltipText={
              "Permet de calculer la prochaine date de la ROP par rapport à la dernière saisie"
            }
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.INTEGER}>
          <PositiveNumberInput
            name="pei.peiDeplacementDistWarn"
            label="Seuil de déplacement d'un PEI import CTP"
            tooltipText={
              "Distance de déplacement du PEI au-delà de laquelle afficher un avertissement lors de " +
              "l'import de Contrôles Techniques Périodiques"
            }
          />
        </AdminParametre>

        <h2 className="mt-4 ms-5">Carte des tournées</h2>
        <AdminParametre type={TYPE_PARAMETRE.INTEGER}>
          <PositiveNumberInput
            name="pei.bufferCarte"
            label="Espace tampon pour la génération de carte (carte des tournées)"
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.BOOLEAN}>
          <CheckBoxInput
            name="pei.peiGenerationCarteTournee"
            label="Active le module de génération de carte des tournées dans le module Point d’eau"
            checked={values?.peiGenerationCarteTournee}
            tooltipText={
              <>
                Le module de génération nécessite qu’une aggrégation de couche
                nommée <b>TOURNEE</b> soit configurée sur Geoserver. Il est
                recommandé que l’aggrégation de couche contienne <i>a minima</i>{" "}
                :
                <ul>
                  <li>Un fond de plan</li>
                  <li>La couche des PEI</li>
                </ul>
                Le module enverra l’identifiant de la tournée via le VIEWPARAM
                tournee_id permettant de discriminer les PEI à retourner
              </>
            }
          />
        </AdminParametre>

        <h2 className="mt-4 ms-5">Déclaration d'un PEI</h2>
        <AdminParametre type={TYPE_PARAMETRE.STRING}>
          <TextAreaInput
            name="pei.declarationPeiDestinataireEmail"
            label="Adresse email qui recevra une notification lors de la déclaration d'un PEI"
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.STRING}>
          <TextAreaInput
            name="pei.declarationPeiObjetEmail"
            label="Objet du mail de notification lors de la déclaration d'un PEI"
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.STRING}>
          <TextAreaInput
            name="pei.declarationPeiCorpsEmail"
            label="Contenu du mail de notification lors de la déclaration d'un PEI"
            tooltipText="Vous pouvez utiliser #[ORGANISME_UTILISATEUR]# et #[LIEN_TELECHARGEMENT]# dans votre message. Ces deux valeurs seront remplacées automatiquement."
          />
        </AdminParametre>
      </>
    )
  );
};

const AdminPeiLongueIndispo = ({
  values,
  setFieldValue,
}: {
  values: ParametresSectionPeiLongueIndispo;
  setFieldValue: (name: string, value: any) => void;
}) => {
  return (
    values && (
      <>
        <AdminParametre type={TYPE_PARAMETRE.STRING}>
          <TextAreaInput
            name="peiLongueIndispo.peiLongueIndisponibiliteMessage"
            label="Message à afficher en cas de PEI indisponible depuis trop longtemps"
            tooltipText={
              "Vous pouvez utiliser #MOIS# et #JOURS# dans votre message. Ces deux valeurs seront remplacées automatiquement."
            }
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.INTEGER}>
          <PositiveNumberInput
            name="peiLongueIndispo.peiLongueIndisponibiliteJours"
            label="Nombre de jours avant de considérer un PEI comme indisponible depuis trop longtemps"
          />
        </AdminParametre>
        <TypeOrganismeMultiSelectParam
          name="peiLongueIndispo.peiLongueIndisponibiliteTypeOrganisme"
          label="Types d'organismes concernés par le message à afficher en cas de PEI indisponible depuis trop longtemps"
          selectedCodes={
            values?.peiLongueIndisponibiliteTypeOrganisme as string[]
          }
          setFieldValue={setFieldValue}
        />
      </>
    )
  );
};

const AdminPermis = ({ values }: { values: ParametresSectionPermis }) => {
  return (
    values && (
      <>
        <AdminParametre type={TYPE_PARAMETRE.INTEGER}>
          <PositiveNumberInput
            name="permis.permisToleranceChargementMetres"
            label="Tolérance de chargement (en m)"
            tooltipText={"TODO"}
          />
        </AdminParametre>
      </>
    )
  );
};

const AdminRcci = ({
  values,
  setFieldValue,
}: {
  values: ParametresSectionRcci;
  setFieldValue: (name: string, value: any) => void;
}) => {
  return (
    values && (
      <>
        <TypeOrganismeMultiSelectParam
          name="rcci.listeTypeOrganismeDdtmOnf"
          label="Types d'organismes dont les utilisateurs seront remontés dans la liste DDTM/ONF dans les RCCI"
          selectedCodes={values?.listeTypeOrganismeDdtmOnf as string[]}
          setFieldValue={setFieldValue}
          required={false}
        />

        <TypeOrganismeMultiSelectParam
          name="rcci.listeTypeOrganismeSdis"
          label="Types d'organismes dont les utilisateurs seront remontés dans la liste SDIS dans les RCCI"
          selectedCodes={values?.listeTypeOrganismeSdis as string[]}
          setFieldValue={setFieldValue}
          required={false}
        />

        <TypeOrganismeMultiSelectParam
          name="rcci.listeTypeOrganismeGendarmerie"
          label="Types d'organismes dont les utilisateurs seront remontés dans la liste Gendarmerie dans les RCCI"
          selectedCodes={values?.listeTypeOrganismeGendarmerie as string[]}
          setFieldValue={setFieldValue}
          required={false}
        />

        <TypeOrganismeMultiSelectParam
          name="rcci.listeTypeOrganismePolice"
          label="Types d'organismes dont les utilisateurs seront remontés dans la liste Police dans les RCCI"
          selectedCodes={values?.listeTypeOrganismePolice as string[]}
          setFieldValue={setFieldValue}
          required={false}
        />
      </>
    )
  );
};

type ActiveOrganismTypesProps = {
  recipientsField: string;
};

const ActiveOrganismTypes = ({ recipientsField }: ActiveOrganismTypesProps) => {
  const [{ value: listeTypeOrganismeIds }, , { setValue }] =
    useField(recipientsField);
  const { data: allOrganismType } = useGet(url`/api/type-organisme/get-active`);

  return (
    <Multiselect
      name={recipientsField}
      label="Types d'organismes concernés qui recevront le courrier"
      options={allOrganismType}
      getOptionValue={(o) => o.typeOrganismeId}
      getOptionLabel={(o) => o.typeOrganismeLibelle}
      value={allOrganismType?.filter((o: { typeOrganismeId: string }) =>
        listeTypeOrganismeIds?.includes(o.typeOrganismeId),
      )}
      onChange={(selection) =>
        setValue(
          selection.map((o: { typeOrganismeId: string }) => o.typeOrganismeId),
        )
      }
    />
  );
};

const AdminUtilisateur = ({
  values,
  setFieldValue,
}: {
  values: ParametresSectionUtilisateur;
  setFieldValue: (name: string, value: any) => void;
}) => {
  const { data: organismeList } = useGet(url`/api/organisme/get-all`);
  const { data: profilUtilisateurList } = useGet(url`/api/profil-utilisateur/`);

  const [groupeFonctionnalitesDeduit, setGroupeFonctionnalitesDeduit] =
    useState<string>();

  const { data: groupeFonctionnalitesWithProfilsList } = useGet(
    url`/api/groupe-fonctionnalites/profils`,
  );

  useEffect(() => {
    setGroupeFonctionnalitesDeduit(
      groupeFonctionnalitesWithProfilsList?.find(
        (e: {
          profilUtilisateurId: string;
          profilOrganismeId: string;
          libelle: string;
        }) =>
          e.profilUtilisateurId ===
            profilUtilisateurList?.find(
              (p: { code: string }) =>
                p.code === values?.profilUtilisateurDefaut,
            )?.id &&
          e.profilOrganismeId ===
            organismeList?.find(
              (e: { code: string }) => e.code === values?.organismeDefaut,
            )?.lienId,
      )?.libelle,
    );
  }, [
    groupeFonctionnalitesWithProfilsList,
    organismeList,
    profilUtilisateurList,
    values?.profilUtilisateurDefaut,
    values?.organismeDefaut,
  ]);

  return (
    values && (
      <>
        <p>
          Ces paramètres vont être appliqués à chaque nouvel utilisateur lors de
          la synchro LDAP, afin qu&apos;il puisse directement se connecter à
          l&apos;application. Attention, si vous saisissez une combinaison
          invalide, la connexion de l&apos;utilisateur n&apos;aboutira pas.
        </p>
        <AdminParametre type={TYPE_PARAMETRE.SELECT}>
          <SelectInput
            name="utilisateur.organismeDefaut"
            label="Organisme par défaut à l'ajout d'un utilisateur"
            options={organismeList}
            getOptionValue={(v) => v.id}
            getOptionLabel={(v) => v.libelle}
            required={false}
            isClearable={true}
            defaultValue={
              organismeList?.find(
                (o: { code: string }) => o.code === values?.organismeDefaut,
              ) ?? undefined
            }
            onChange={(organisme) => {
              setFieldValue(
                `utilisateur.organismeDefaut`,
                organismeList?.find(
                  (o: { id: string }) => o.id === organisme?.id,
                )?.code,
              );
            }}
          />
        </AdminParametre>
        <AdminParametre type={TYPE_PARAMETRE.SELECT}>
          <SelectInput
            name="utilisateur.profilUtilisateurDefaut"
            label="Profil utilisateur par défaut à l'ajout d'un utilisateur"
            options={profilUtilisateurList}
            getOptionValue={(v) => v.id}
            getOptionLabel={(v) => v.libelle}
            required={false}
            isClearable={true}
            defaultValue={
              profilUtilisateurList?.find(
                (p: { code: string }) =>
                  p.code === values?.profilUtilisateurDefaut,
              ) ?? undefined
            }
            onChange={(profilUtilisateur) => {
              setFieldValue(
                `utilisateur.profilUtilisateurDefaut`,
                profilUtilisateurList?.find(
                  (p: { id: string }) => p.id === profilUtilisateur?.id,
                )?.code,
              );
            }}
          />
        </AdminParametre>
        <div className="text-center">
          {groupeFonctionnalitesDeduit != null ? (
            <>
              Le groupe de fonctionnalités qui sera affecté sera :{" "}
              <b>{groupeFonctionnalitesDeduit}</b>
            </>
          ) : (
            "Aucun groupe de fonctionnalités trouvé."
          )}
        </div>
        <AdminParametre type={TYPE_PARAMETRE.BOOLEAN}>
          <CheckBoxInput
            name="utilisateur.organismeProfilMajSynchro"
            label="Mettre à jour l'organisme et le profil de l'utilisateur lors de la synchronisation utilisateur"
            checked={values?.organismeProfilMajSynchro}
            tooltipText="Si activé, l'organisme et le profil de l'utilisateur seront mis à jour lors de chaque synchronisation à partir du LDAP. Les attributs Keycloak 'organismeCode' et 'profilUtilisateurCode' doivent être présents (configurez un mapper LDAP dans Keycloak pour mapper vos attributs LDAP vers 'organismeCode' et 'profilUtilisateurCode'). Si l'un de ces attributs est absent, l'utilisateur sera désactivé. Si le paramètre est désactivé, seules les données personnelles (nom, prénom, email) seront mises à jour, tout en conservant l'organisme et le profil affectés dans REMOcRA."
          ></CheckBoxInput>
        </AdminParametre>
      </>
    )
  );
};

const AdminCourrier = ({ values }: { values: ParametresSectionCourrier }) => {
  return (
    values && (
      <>
        <AdminParametre type={TYPE_PARAMETRE.BOOLEAN}>
          <CheckBoxInput
            name="courrier.courrierRestrictionZC"
            label="Permettre à l'utilisateur de restreindre la recherche des destinataires sur sa zone de compétence"
            checked={values?.courrierRestrictionZC}
            tooltipText={
              "Si ce paramètre est activé, la notification d’un courrier affichera une case à cocher permettant de limiter la recherche des destinataires à la zone de compétence de l’utilisateur connecté."
            }
          />
        </AdminParametre>
      </>
    )
  );
};
